import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Auth, clearTokens } from "../services/api";
import { FaSignOutAlt, FaUserCircle, FaChevronDown, FaBars } from "react-icons/fa";
import logo from "../logo.svg";

const DashboardNavbar = ({ onStoreChange, toggleSidebar, isSidebarOpen }) => {
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const timeoutRef = useRef(null);

  const handleStoreSelection = (event) => {
    onStoreChange(event.target.value);
  };

  const handleLogout = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    try {
      if (refreshToken) {
        await Auth.logout(refreshToken);
      }
    } catch (err) {
      console.warn("Logout request failed:", err);
    } finally {
      clearTokens();
      navigate("/login", { replace: true });
    }
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsUserMenuOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsUserMenuOpen(false);
    }, 200); // small delay to prevent flickering
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const userEmail = localStorage.getItem("userEmail") || "User";

  return (
    <nav className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-6 shadow-sm sticky top-0 z-40 w-full">
      {/* Left Side: Hamburger & Logo */}
      <div className="flex items-center space-x-4">
        <button 
          onClick={toggleSidebar}
          className="text-gray-500 hover:text-blue-600 focus:outline-none transition-colors p-2 rounded-full hover:bg-gray-100"
          title={isSidebarOpen ? "Close Sidebar" : "Open Sidebar"}
          style={{ display: isSidebarOpen ? 'none' : 'block' }}
        >
          <FaBars size={20} />
        </button>
        <div className="flex items-center" style={{ display: isSidebarOpen ? 'none' : 'flex' }}>
           <img src={logo} alt="AppOptimax" className="h-8 w-auto" />
           <span className="ml-2 text-xl font-bold text-gray-800 hidden md:block">AppOptimax</span>
        </div>
      </div>

      {/* Center: Store Selection */}
      <div className="flex-1 flex justify-center">
         <div className="flex items-center bg-gray-100 rounded-lg px-4 py-2 border border-gray-200 shadow-inner">
          <span className="text-xs font-bold text-gray-500 mr-3 uppercase tracking-wider">Select Your Store:</span>
          <select 
            onChange={handleStoreSelection} 
            className="bg-transparent border-none text-gray-800 text-sm font-semibold focus:ring-0 cursor-pointer py-0 pl-0 pr-8 appearance-none"
          >
            <option value="play-store">Google Play Store</option>
            <option value="app-store">Apple App Store</option>
          </select>
          <FaChevronDown className="text-gray-400 text-xs -ml-6 pointer-events-none" />
        </div>
      </div>

      {/* Right Side: User Menu */}
      <div className="flex items-center space-x-6">
        <div 
          className="relative"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <button 
            className="flex items-center space-x-2 focus:outline-none p-1 rounded-lg hover:bg-gray-50 transition-colors"
            onClick={toggleUserMenu}
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 flex items-center justify-center text-white shadow-md ring-2 ring-white">
               <FaUserCircle className="text-xl" />
            </div>
             <FaChevronDown className={`text-xs text-gray-400 transition-transform duration-200 ${isUserMenuOpen ? 'transform rotate-180' : ''}`} />
          </button>

          {/* Dropdown */}
          {isUserMenuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 border border-gray-100 z-50 animate-fade-in-down">
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-xs text-gray-500">Signed in as</p>
                <p className="text-sm font-semibold text-gray-800 truncate" title={userEmail}>{userEmail}</p>
              </div>
              <button 
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2 transition-colors"
              >
                <FaSignOutAlt className="text-xs" />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default DashboardNavbar;
